import {GQLResponse, GQLSubscribeRequests} from "../gqlModel/types";
import {action, computed, makeObservable, observable} from 'mobx';

import {IGraphQLSubscriptionClient} from "../gqlModel/IGraphQLSubscriptionClient";
import {ModelSortDirection} from "../../API";
import {ColId} from "../../ui/components/Table";
import {Cancelable, debounce} from "../../utils/debounce";
import {logErrorToSentry} from "../../utils/errors/logErrorToSentry";
import {sortByField} from "../../utils/sortByField";
import {cacheManager} from "@stores/cache/CacheManager";
import {createCacheMetadata} from "@stores/cache/utils";
import {BasicDataSourceDeps} from "../../platform/services/types/DataSourceDependencies";


export type FilterType = 'server' | 'client';

export interface FilterDefinition<T> {
    type: FilterType;
    apply: (row: T) => boolean, /* required even for server filter as a fallback on case when we have all data loaded */
}

export interface SortRule {
    field: string;
    direction: ModelSortDirection;
}

export type FilterSchema<T> = Record<ColId, FilterDefinition<T>>;


export abstract class DataSource<T extends Record<string, any> = any, D extends Record<string, any> = T, DEPS extends BasicDataSourceDeps = BasicDataSourceDeps> {
    NO_FILTER_HASH = '';
    isFullyLoaded: boolean = false;
    isInitialized: boolean = false;
    _initialLoadPromise: Promise<void> | null = null
    isLoading: boolean = false; /* to reflect UI */
    isLoadingMore: boolean = false; /* to reflect UI */
    isRefreshing: boolean = false; /* indicates background refresh of cached data */
    isShowingCachedData: boolean = false; /* indicates UI is showing cached data */
    filterValues: Record<ColId, any>; /* actually not only ColId but rather field name on the server record */
    nextToken?: string | null = null;
    _allRows: D[] = [];
    sortRule?: SortRule = undefined;
    abstract readonly _modelName: string;
    readonly _subscriptionGQLs?: GQLSubscribeRequests;

    _isLoadingAllRows: boolean = false;
    _wsClient?: IGraphQLSubscriptionClient;
    onItemUpdate?: (item: T) => void;
    onItemDelete?: (item: T) => void;
    onItemCreate?: (item: T) => void;
    _isDestroyed: boolean = false;

    _loadAllRowsDebounced: (() => void) & Cancelable;
    _applyServerFilterDebounced: (() => void) & Cancelable;

    _latestRequestedFilterHash?: string;

    _allRecordsWithNoFilter?: D[]
    _allRecordsWithNoFilterNextToken?: string | null;
    _latestRequestedAbortController?: AbortController;

    protected deps: DEPS;

    // Caching properties
    protected _cacheEnabled: boolean = true;
    protected _currentUserId?: string;
    protected _hasDataFromCache: boolean = false;
    protected _rawServerData: T[] = []; // Track raw server data for caching

    constructor(deps: DEPS) {
        this.deps = deps;
        this.filterValues = this.getFilterDefaultValues();
        makeObservable(this, {
            filterValues: observable,
            setFilterValue: action,
            resetFilters: action,

            isLoading: observable,
            setIsLoading: action,

            isRefreshing: observable,
            setIsRefreshing: action,

            isShowingCachedData: observable,
            setIsShowingCachedData: action,

            sortRule: observable,
            setSortRule: action,

            isInitialized: observable,
            setIsInitialized: action,

            isLoadingMore: observable,
            setIsLoadingMore: action,

            _allRows: observable,
            _setAllRows: action,

            filteredRows: computed,
            allRows: computed,
        });

        this._loadAllRowsDebounced = debounce(this._loadAllRows);
        this._applyServerFilterDebounced = debounce(this._applyServerFilter);
    }

    abstract getWsClient(): Promise<IGraphQLSubscriptionClient | undefined>;

    abstract loadGQLFetch(filterValues: Record<ColId, any>): Promise<GQLResponse<T>>;

    abstract loadMoreGQLFetch(nextToken: string, filterValues: Record<ColId, any>): Promise<GQLResponse<T>>;

    abstract get syncHash(): string;

    get syncQueryFilter() {
        return {};
    }

    get allRows() {
        return this._allRows;
    }

    setSortRule(field: string, direction: ModelSortDirection = ModelSortDirection.ASC) {
        this.sortRule = {
            field: field,
            direction: direction,
        }
    }

    toggleSort = (field: string) => {
        let targetDirection = ModelSortDirection.ASC;
        if (this.sortRule?.field === field) {
            targetDirection = this.sortRule.direction === ModelSortDirection.DESC
                ? ModelSortDirection.ASC
                : ModelSortDirection.DESC
        }
        this.setSortRule(
            field,
            targetDirection
        )
    }

    getFilterSchema(): FilterSchema<D> {
        return {};
    };

    setFilterValue(colId: string, value: any) {
        const filterSchema = this.getFilterSchema();
        if (filterSchema[colId]) {
            const filterDefinition = filterSchema[colId];
            
            // Check if this is a server filter change that would affect cache
            if (filterDefinition.type === 'server') {
                const oldServerFilterHash = this._getFilterHash(this.filterValues, 'server');
                const newFilterValues = { ...this.filterValues, [colId]: value };
                const newServerFilterHash = this._getFilterHash(newFilterValues, 'server');
                
                if (oldServerFilterHash !== newServerFilterHash) {
                    // Server filter changed, invalidate cache
                    this.invalidateCacheForServerFilters();
                    // Reset cache flag and raw data since filters changed
                    this._hasDataFromCache = false;
                    this._rawServerData = [];
                }
                
                if (!this.isFullyLoaded) {
                    this._applyServerFilterDebounced();
                }
            } else if (filterDefinition.type === 'client' && !this.isFullyLoaded) {
                this._loadAllRowsDebounced();
            }

            this.filterValues[colId] = value;
        } else {
            throw new Error(`filter for ${colId} is not supported`);
        }
    }

    resetFilters() {
        const oldServerFilterHash = this._getFilterHash(this.filterValues, 'server');
        const newFilterValues = this.getFilterDefaultValues();
        const newServerFilterHash = this._getFilterHash(newFilterValues, 'server');
        
        if (oldServerFilterHash !== newServerFilterHash) {
            // Server filters changed, invalidate cache
            this.invalidateCacheForServerFilters();
            // Reset cache flag and raw data since filters changed
            this._hasDataFromCache = false;
            this._rawServerData = [];
        }
        
        this.filterValues = newFilterValues;
    }

    setIsLoading(isLoading: boolean) {
        this.isLoading = isLoading;
    }

    setIsRefreshing(isRefreshing: boolean) {
        this.isRefreshing = isRefreshing;
    }

    setIsShowingCachedData(isShowingCachedData: boolean) {
        this.isShowingCachedData = isShowingCachedData;
    }

    setIsLoadingMore(isLoadingMore: boolean) {
        this.isLoadingMore = isLoadingMore;
    }

    setIsInitialized(isInitialized: boolean) {
        this.isInitialized = isInitialized;
    }

    fixNO_FILTER_HASH(filterValues: Record<string, any>) {
        this.NO_FILTER_HASH = this._getFilterHash(filterValues);
    }

    getFilterDefaultValues() {
        return {};
    }

    getFilterableValue = (field: string, obj: D): string | number | boolean | null | undefined => {
        return obj[field] as string | number | boolean | null;
    };

    get filteredRows() {
        let resultRows = this.allRows.slice();
        const filterSchema = this.getFilterSchema();
        for (const colId in filterSchema) {
            if (this.filterValues[colId] !== null) {
                resultRows = resultRows.filter(filterSchema[colId].apply);
            }
        }
        if (this.sortRule) {
            resultRows = sortByField(resultRows, this.sortRule.field, this.sortRule.direction, false, this.getFilterableValue);
        }
        return resultRows;
    }

    async initialLoad(loadAllRows = true) {
        if (this._initialLoadPromise === null) {
            this._initialLoadPromise = this._initialLoad(loadAllRows);
        }
        return this._initialLoadPromise;
    }

    async _initialLoad(loadAllRows = true) {
        try {
            // Validate cache for current user session
            await this.validateCacheForCurrentUser();
            
            // Initialize user ID for caching
            await this.getCurrentUserId();
            
            // Try to load from cache first
            const cachedData = await this.loadFromCache();
            if (cachedData && cachedData.length > 0) {
                // We have cache data - show it immediately and mark as initialized
                this._setAllRows(cachedData);
                this.setIsInitialized(true);
                this.setIsShowingCachedData(true);
                this._initSync();
                
                // Continue with server requests in background (don't await)
                this._backgroundServerSync(loadAllRows);
                
                return; // Return immediately so UI can show cached data
            }
            
            // No cache data - proceed with normal server loading
            const result = await this._applyServerFilter();
            if (result) {
                this.setIsInitialized(true);
                if (loadAllRows) {
                    await this._loadAllRows();
                }
                this._initSync();
            }
        } catch (error: any) {
            logErrorToSentry(error);
            this._initialLoadPromise = null;
        }
    }

    async loadMore() {
        if (!this.nextToken || this.isLoadingMore) {
            return false;
        }
        this.setIsLoadingMore(true);
        const ourRequestAbortController = new AbortController();
        this._cancelRequestIfPresent(ourRequestAbortController);

        const onAbort = () => {
            this.setIsLoadingMore(false);
        };
        ourRequestAbortController.signal.addEventListener('abort', onAbort);

        try {
            const response = await this.loadMoreGQLFetch(this.nextToken, this.filterValues);
            if (!ourRequestAbortController.signal.aborted) {
                this.nextToken = response.nextToken;
                const updatedRows = [...this._allRows, ...response.items.map(this.mapServerResponseToLocalRow)];
                this._setAllRows(updatedRows);
                
                // Note: Don't save to cache here - let _loadAllRows handle complete data caching
                // This is just incremental loading, cache should only store complete datasets
                
                this.setIsLoadingMore(false);
            }
        } catch (error: any) {
            if (!ourRequestAbortController.signal.aborted) {
                this.setIsLoadingMore(false);
            }
            console.error(error);
        } finally {
            ourRequestAbortController.signal.removeEventListener('abort', onAbort);
        }
        return true;
    }

    /*=========================================== inner implementation =============================================*/

    /*------------- sync logic ---------------*/
    async _initSync() {
        if (!this._subscriptionGQLs) {
            return;
        }
        this._wsClient = await this.getWsClient();
        if (this._wsClient && !this._isDestroyed) {
            this._wsClient.registerSubscription(`onCreate${this._modelName}${this.syncHash}`, this._subscriptionGQLs.onCreate, this._onCreate, this.syncQueryFilter);
            this._wsClient.registerSubscription(`onUpdate${this._modelName}${this.syncHash}`, this._subscriptionGQLs.onUpdate, this._onUpdate, this.syncQueryFilter);
            this._wsClient.registerSubscription(`onDelete${this._modelName}${this.syncHash}`, this._subscriptionGQLs.onDelete, this._onDelete, this.syncQueryFilter);
        }
    }

    _onCreate = (createdItem: T) => {
        if (!createdItem) {
            return;
        }
        this._setAllRows([this.mapServerResponseToLocalRow(createdItem), ...this._allRows]);

        if (this.onItemCreate) {
            this.onItemCreate(createdItem);
        }
    }

    _onUpdate = (updatedItem: T) => {
        if (!updatedItem) {
            return;
        }
        const updatedArray = this._allRows.slice();
        // @ts-ignore
        const index = updatedArray.findIndex(item => item.id === updatedItem.id);
        if (index !== -1) {
            updatedArray[index] = this.mapServerResponseToLocalRow(updatedItem);
            this._setAllRows(updatedArray);
        }
        if (this.onItemUpdate) {
            this.onItemUpdate(updatedItem);
        }
    }

    _onDelete = (deletedItem: T) => {
        if (!deletedItem) {
            return;
        }
        // @ts-ignore
        this._setAllRows(this._allRows.filter(item => item.id !== deletedItem.id));

        if (this.onItemDelete) {
            this.onItemDelete(deletedItem);
        }
    }

    mapServerResponseToLocalRow(row: T) {
        return row as unknown as D;
    }

    /*----------- sync logic end -------------*/

    _setAllRows(_allRows: D[]) {
        this._allRows = _allRows.filter(row => (row as any)._deleted !== true);
    }

    _getFilterHash(filterValues = this.filterValues, type: 'server' | 'client' | 'all' = 'server') {
        let filterHash = '';
        const filterSchema = this.getFilterSchema();

        for (const colId in filterSchema) {
            const schema = filterSchema[colId];
            const value = filterValues[colId];

            if (value == null) continue;

            const isServer = schema.type === 'server';
            const isClient = schema.type === 'client';

            if (
                (type === 'server' && isServer) ||
                (type === 'client' && isClient) ||
                (type === 'all')
            ) {
                filterHash += `${colId}#${JSON.stringify(value)}`;
            }
        }

        return filterHash;
    }

    async _loadAllRows() {
        if (this.isFullyLoaded) {
            return true;
        }
        const currentRequestHash = this._getFilterHash();
        if (!this.isInitialized || (this._isLoadingAllRows && currentRequestHash === this._latestRequestedFilterHash)) {
            return false;
        }

        this._isLoadingAllRows = true;
        this._latestRequestedFilterHash = currentRequestHash;

        const ourRequestAbortController = new AbortController();
        this._cancelRequestIfPresent(ourRequestAbortController);
        this.setIsLoadingMore(true)

        const onAbort = () => {
            this.setIsLoadingMore(false);
            this._isLoadingAllRows = false;
        };
        ourRequestAbortController.signal.addEventListener('abort', onAbort);

        try {
            // Start with existing data (either from cache or previous server load)
            let accumulatedRows = this._hasDataFromCache ? [] : [...this._allRows];
            let accumulatedRawData = [...this._rawServerData]; // Start with existing raw data
            
            // If we're replacing cached data, start fresh
            if (this._hasDataFromCache) {
                accumulatedRows = [...this._allRows];
                // Reset raw data since we're starting fresh
                accumulatedRawData = [];
            }
            
            while (this.nextToken) {
                const response = await this.loadMoreGQLFetch(this.nextToken, this.filterValues);
                if (!ourRequestAbortController.signal.aborted) {
                    this.nextToken = response.nextToken;
                    // Accumulate both mapped and raw data
                    accumulatedRows = [...accumulatedRows, ...response.items.map(this.mapServerResponseToLocalRow)];
                    accumulatedRawData = [...accumulatedRawData, ...response.items];
                } else {
                    break;
                }
            }

            if (!ourRequestAbortController.signal.aborted) {
                // Update UI with all accumulated data at once
                this._setAllRows(accumulatedRows);
                
                // Update raw data tracking
                this._rawServerData = accumulatedRawData;
                
                // Save complete raw data to cache
                await this.saveToCache(this._rawServerData, !!this.nextToken);
                
                // Reset cache flag since we now have fresh server data
                this._hasDataFromCache = false;
                
                this._isLoadingAllRows = false;
                this.setIsLoadingMore(false);
                if (currentRequestHash === this.NO_FILTER_HASH) {
                    this._pinFullyLoaded();
                }
            }
        } catch (error: any) {
            if (!ourRequestAbortController.signal.aborted) {
                this.setIsLoadingMore(false);
                this._isLoadingAllRows = false;
            }
            console.error(error);
        } finally {
            ourRequestAbortController.signal.removeEventListener('abort', onAbort);
        }
        return true;
    }

    _pinFullyLoaded() {
        this.isFullyLoaded = true;
        this._allRecordsWithNoFilter = this._allRows;
        this._allRecordsWithNoFilterNextToken = this.nextToken;
    }

    async _applyServerFilter(skipCache: boolean = false) {
        const currentRequestHash = this._getFilterHash();
        if (currentRequestHash === this._latestRequestedFilterHash) {
            return false;
        }

        // Initialize user ID for caching
        await this.getCurrentUserId();

        // Try to load from cache first (show immediately if available)
        // Skip cache if this is called from background sync to avoid duplicate loading
        if (!skipCache) {
            const cachedData = await this.loadFromCache();
            if (cachedData && cachedData.length > 0) {
                this._setAllRows(cachedData);
                this.setIsShowingCachedData(true);
                // Continue with server request to get fresh data
            }
        }

        this.setIsLoading(true)
        const ourRequestAbortController = new AbortController();
        this._cancelRequestIfPresent(ourRequestAbortController);
        const onAbort = () => {
            this.setIsLoading(false);
        };
        ourRequestAbortController.signal.addEventListener('abort', onAbort);
        let shouldSkipQuery = false;

        if (this._latestRequestedFilterHash === this.NO_FILTER_HASH && !this.isFullyLoaded) {
            /* if we were on clear filters preserve available rows */
            this._allRecordsWithNoFilterNextToken = this.nextToken;
            this._allRecordsWithNoFilter = this._allRows;
        } else if (currentRequestHash === this.NO_FILTER_HASH && this._allRecordsWithNoFilter) {
            /* if we return to clear filters restore available rows without a server request */
            this.nextToken = this._allRecordsWithNoFilterNextToken;
            this._setAllRows(this._allRecordsWithNoFilter);
            shouldSkipQuery = true;
        }

        this._latestRequestedFilterHash = currentRequestHash;

        try {
            if (!shouldSkipQuery) {
                const response = await this.loadGQLFetch(this.filterValues);
                if (!ourRequestAbortController.signal.aborted) {
                    this.nextToken = response.nextToken;
                    const freshData = response.items.map(this.mapServerResponseToLocalRow);
                    this._setAllRows(freshData);

                    // Track raw server data for caching
                    this._rawServerData = response.items;

                    // Save fresh data to cache (only if this is complete data, not partial)
                    if (!this.nextToken) {
                        // No more pages, this is complete data - save raw server response
                        await this.saveToCache(this._rawServerData, false);
                    }
                    // If there are more pages, let _loadAllRows handle the complete caching

                    // Reset cache flag and indicators since we now have fresh server data
                    this._hasDataFromCache = false;
                    this.setIsShowingCachedData(false);

                    if (currentRequestHash === this.NO_FILTER_HASH && this.nextToken === null) {
                        this._pinFullyLoaded();
                    }
                }
            }
            if (!ourRequestAbortController.signal.aborted) {
                this.setIsLoading(false);
                this._latestRequestedAbortController = undefined;
            }
        } catch (error: any) {
            if (!ourRequestAbortController.signal.aborted) {
                this.setIsLoading(false);
            }
            console.error(error);
            this._latestRequestedFilterHash = undefined;
            throw error;
        } finally {
            ourRequestAbortController.signal.removeEventListener('abort', onAbort);
        }
        return true;
    }

    _cancelRequestIfPresent(newAbortController: AbortController) {
        if (this._latestRequestedAbortController) {
            this._latestRequestedAbortController.abort();
        }
        this._latestRequestedAbortController = newAbortController;
    }

    destroy() {
        this._isDestroyed = true;
        if (this._wsClient) {
            this._wsClient.cancelSubscription(`onCreate${this._modelName}${this.syncHash}`);
            this._wsClient.cancelSubscription(`onUpdate${this._modelName}${this.syncHash}`);
            this._wsClient.cancelSubscription(`onDelete${this._modelName}${this.syncHash}`);
        }
    }

    /*=========================================== caching methods =============================================*/
    
    /**
     * Caching Strategy:
     * - Cache raw server response data (T[]) to avoid MobX proxy serialization issues
     * - Apply mapServerResponseToLocalRow() after reading from cache
     * - Track _rawServerData to maintain original server response for caching
     * - Only cache complete datasets, not partial pagination chunks
     */

    protected async getCurrentUserId(): Promise<string | undefined> {
        if (!this._currentUserId) {
            const user = await this.deps.authService.getCurrentUser();
            this._currentUserId = user?.sub;
        }
        return this._currentUserId;
    }

    protected getCacheKey(): string {
        const serverFilterHash = this._getFilterHash(this.filterValues, 'server');
        const userId = this._currentUserId || 'unknown';
        return `${userId}_${this._modelName}_${this.syncHash}_${serverFilterHash}`;
    }

    protected async loadFromCache(): Promise<D[] | null> {
        if (!this._cacheEnabled) {
            return null;
        }

        try {
            const cacheKey = this.getCacheKey();
            const cachedData = await cacheManager.get<T[]>(cacheKey); // Cache raw server response
            
            if (cachedData) {
                // Restore pagination state from cache
                if (cachedData.metadata.nextToken !== undefined) {
                    this.nextToken = cachedData.metadata.nextToken;
                }
                
                // Mark that we have data from cache
                this._hasDataFromCache = true;
                
                // Map raw server response to local rows after reading from cache
                return cachedData.data.map(item => this.mapServerResponseToLocalRow(item));
            }
            
            return null;
        } catch (error) {
            console.warn('Failed to load from cache:', error);
            return null;
        }
    }

    protected async saveToCache(rawServerData: T[], isPartialData: boolean = false): Promise<void> {
        if (!this._cacheEnabled) {
            return;
        }

        try {
            const userId = await this.getCurrentUserId();
            if (!userId) {
                return;
            }

            const cacheKey = this.getCacheKey();
            const serverFilterHash = this._getFilterHash(this.filterValues, 'server');
            
            const metadata = createCacheMetadata(
                userId,
                this.syncHash,
                serverFilterHash,
                this._modelName,
                isPartialData,
                this.nextToken
            );
            
            // Cache raw server response to avoid MobX proxy issues
            await cacheManager.set(cacheKey, rawServerData, metadata);
            
        } catch (error) {
            console.warn('Failed to save to cache:', error);
        }
    }

    protected shouldUseCache(): boolean {
        return this._cacheEnabled && !this.isLoading;
    }

    async validateCacheForCurrentUser(): Promise<void> {
        try {
            const userId = await this.getCurrentUserId();
            if (userId) {
                await cacheManager.validateUserSession(userId);
            }
        } catch (error) {
            console.warn('Failed to validate cache for current user (cache will be ignored):', error);
            // Disable cache for this session if validation fails
            this._cacheEnabled = false;
        }
    }

    protected async invalidateCacheForServerFilters(): Promise<void> {
        if (!this._cacheEnabled) {
            return;
        }

        try {
            const userId = await this.getCurrentUserId();
            if (!userId) {
                return;
            }

            // Clear cache entries for this DataSource and user
            const pattern = `${userId}_${this.syncHash}`;
            await cacheManager.clear(pattern);
            
            console.log(`Invalidated cache for ${this._modelName} with pattern:`, pattern);
        } catch (error) {
            console.warn('Failed to invalidate cache:', error);
        }
    }

    /**
     * Performs server sync in background after showing cached data
     */
    private async _backgroundServerSync(loadAllRows: boolean = true): Promise<void> {
        try {
            this.setIsRefreshing(true);
            
            // Perform server filter request to get fresh data (skip cache since we already loaded it)
            const result = await this._applyServerFilter(true);
            
            // Load all rows if requested and server request was successful
            if (result && loadAllRows) {
                await this._loadAllRows();
            }
            
            // Clear cached data indicators since we now have fresh data
            this.setIsShowingCachedData(false);
            this.setIsRefreshing(false);
            
        } catch (error: any) {
            this.setIsRefreshing(false);
            console.warn(`Background sync failed for ${this._modelName}:`, error);
            logErrorToSentry(error);
        }
    }
}
